import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { LOGO_SRC } from '../data/assets';

/**
 * The social card, rendered at exactly 1200x630 so it can be screenshotted and
 * hosted as og.png. Not linked from anywhere and deliberately outside the site
 * frame — no nav, no footer, no registration marks.
 *
 * To capture: open /og, screenshot just the card, save as og.png, host it, then
 * point OG_IMAGE in data/seo.ts at the URL.
 */
export function OgCard() {
  const cardRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  async function download() {
    if (!cardRef.current) return;
    setState('working');
    try {
      const url = await toPng(cardRef.current, {
        width: 1200,
        height: 630,
        pixelRatio: 2,
        cacheBust: true
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = 'og.png';
      a.click();
      setState('done');
    } catch {
      setState('error');
    }
  }
  return <div style={{
    minHeight: '100vh',
    background: '#E9EDF1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    padding: 40
  }} data-id="element-647" data-mp="9wsy4m6ktorr">
      <div id="og-card" ref={cardRef} style={{
      width: 1200,
      height: 630,
      flex: 'none',
      background: 'var(--bg)',
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid var(--line)',
      display: 'grid',
      gridTemplateRows: 'auto 1fr auto'
    }} data-id="element-648" data-mp="jm2n3t64irbn">
        {/* Vertical rule at the same proportion as the page's hero divider, so
            the card reads as a crop of the site rather than a separate artifact. */}
        <span aria-hidden="true" style={{
        position: 'absolute',
        left: 760,
        top: 0,
        bottom: 0,
        width: 1,
        background: 'var(--line)'
      }} data-id="element-649" data-mp="1cb9nkxp51qzj" />
        <span aria-hidden="true" style={{
        position: 'absolute',
        left: 760,
        top: 96,
        width: 13,
        height: 13,
        transform: 'translate(-50%,-50%)',
        background: 'linear-gradient(var(--line-2),var(--line-2)) center/13px 1px no-repeat, linear-gradient(var(--line-2),var(--line-2)) center/1px 13px no-repeat'
      }} data-id="element-650" data-mp="hnrjnb0hcvh2" />

        <header style={{
        height: 96,
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 56px',
        position: 'relative',
        zIndex: 1,
        background: 'var(--bg)'
      }} data-id="element-651" data-mp="dmpk0t2nihek">
          <img src={LOGO_SRC} alt="Synthia" style={{
          height: 30,
          display: 'block',
          mixBlendMode: 'multiply'
        }} data-id="element-652" data-mp="3ccfyyjbsp6lo" />
          <span style={{
          fontFamily: 'var(--mono)',
          fontSize: 13,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--ink-2)'
        }} data-id="element-653" data-mp="1wlnflbgl7b3d">
            The Instruction Layer
          </span>
        </header>

        <div style={{
        padding: '0 56px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 1
      }} data-id="element-654" data-mp="2amgee8dx8sca">
          <h1 style={{
          fontFamily: 'var(--disp)',
          fontWeight: 500,
          fontSize: 62,
          lineHeight: 1.04,
          letterSpacing: '-.045em',
          color: 'var(--ink)',
          maxWidth: '17ch'
        }} data-id="element-655" data-mp="25m24slwo9ac5">
            Your best judgment. Built into every answer that matters.
          </h1>
          <p style={{
          marginTop: 26,
          fontSize: 22,
          lineHeight: 1.5,
          color: 'var(--ink-2)',
          maxWidth: '44ch'
        }} data-id="element-656" data-mp="1ag4eli7lx5ee">
            What&rsquo;s binding goes first &mdash; seated before the model answers, with a signed
            receipt for what it was told.
          </p>
        </div>

        <div style={{
        borderTop: '1px solid var(--line)',
        padding: '22px 56px',
        display: 'flex',
        gap: 28,
        fontFamily: 'var(--mono)',
        fontSize: 15,
        letterSpacing: '.04em',
        color: 'var(--mut)',
        position: 'relative',
        zIndex: 1,
        background: 'var(--bg)'
      }} data-id="element-657" data-mp="1tc4b96bj4x4b">
          <span data-id="element-658" data-mp="7x82d3dh6v4s">
            the model, <b style={{
            color: 'var(--ink)',
            fontWeight: 600
          }} data-id="element-659" data-mp="3xfzn7arjj7c">you rent</b>
          </span>
          <span data-id="element-660" data-mp="2flkw2vbmlekk">&middot;</span>
          <span data-id="element-661" data-mp="2fmcjhiv2k5zr">
            the engine, <b style={{
            color: 'var(--ink)',
            fontWeight: 600
          }} data-id="element-662" data-mp="2fj83zpc5lnts">you subscribe to</b>
          </span>
          <span data-id="element-663" data-mp="2jkto9vr0p4e0">&middot;</span>
          <span data-id="element-664" data-mp="2om9avs8ufiyi">
            the instructions, <b style={{
            color: 'var(--blue-ink)',
            fontWeight: 600
          }} data-id="element-665" data-mp="2ol7m6kywmxyb">you own</b>
          </span>
        </div>
      </div>

      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16
    }} data-id="element-666" data-mp="2te0q56mju29c">
        <button type="button" className="btn g" onClick={download} disabled={state === 'working'} data-id="element-667" data-mp="2yar7rye055wv">
          {state === 'working' ? 'Preparing\u2026' : 'Download og.png'}
        </button>
        <span style={{
        fontFamily: 'var(--mono)',
        fontSize: 12,
        letterSpacing: '.05em',
        color: state === 'error' ? '#b8492e' : 'var(--ink-2)'
      }} data-id="element-668" data-mp="37c8wu69e0hxp">
          {state === 'done' ? 'Saved to your Downloads \u2014 upload it in chat' : state === 'error' ? 'Couldn\u2019t render \u2014 try once more' : '1200\u00d7630 \u00b7 saves to your Downloads folder'}
        </span>
        <Link className="btn o" to="/" data-id="element-669" data-mp="2ga57zvjkjlhn">
          &larr; Back to the site
        </Link>
      </div>
    </div>;
}